'use client';

import { useMemo } from 'react';

import { api, useQuery } from '@/lib/convex';
import { workspaceFilesHref } from '@/lib/workspace-files-route';
import type { LinkResult, LinkScope } from '../types';

const LINK_LIMIT = 20;

function matches(term: string, ...values: Array<string | undefined>) {
  if (!term) return true;
  const lower = term.toLocaleLowerCase();
  return values.some((value) => value?.toLocaleLowerCase().includes(lower));
}

/**
 * Results for the active link scope. Each source is a bounded list the
 * workspace already loads elsewhere, filtered by the typed term here;
 * References use their server-side search index. Only the active scope's
 * query runs.
 */
export function useLinkScopeSearch({
  scope,
  orgSlug,
  term,
}: {
  scope: LinkScope | null;
  orgSlug?: string;
  term: string;
}): { results: LinkResult[]; isPending: boolean } {
  const active = (target: LinkScope) => scope === target && Boolean(orgSlug);
  const slug = orgSlug ?? '';
  const needsOrgId = active('connectors') || active('downloads');
  const organization = useQuery(
    api.organizations.queries.getBySlug,
    needsOrgId ? { slug } : 'skip'
  );
  const organizationId = organization.data?._id;

  const connectors = useQuery(
    api.composio_extensions.queries.listComposioConnectorCards,
    active('connectors') && organizationId ? { organizationId } : 'skip'
  );
  // Drive file and folder names: a server search, so it waits for 2+ chars.
  const files = useQuery(
    api.vfs.public.searchByName,
    active('files') && term.trim().length >= 2
      ? { orgSlug: slug, searchTerm: term, limit: LINK_LIMIT }
      : 'skip'
  );
  const brands = useQuery(
    api.vfs.public.listDirectory,
    active('brands')
      ? {
          orgSlug: slug,
          path: `/${slug}/brands`,
          paginationOpts: { numItems: 50, cursor: null },
        }
      : 'skip'
  );
  const drives = useQuery(
    api.drives.queries.listAccessibleDrives,
    active('drives') ? { orgSlug: slug } : 'skip'
  );
  const shared = useQuery(
    api.vfs.sharing_public.sharedWithMe,
    active('shared') ? { orgSlug: slug } : 'skip'
  );
  const references = useQuery(
    api.references.queries.list,
    active('references')
      ? {
          orgSlug: slug,
          searchTerm: term.trim().length >= 2 ? term.trim() : undefined,
          limit: LINK_LIMIT,
        }
      : 'skip'
  );
  const channels = useQuery(
    api.organizations.channels.queries.listChannels,
    active('channels') ? { orgSlug: slug } : 'skip'
  );
  const downloads = useQuery(
    api.download_jobs.queries.getDownloadManagerFeed,
    active('downloads') && organizationId ? { organizationId } : 'skip'
  );

  const results = useMemo((): LinkResult[] => {
    if (!scope || !orgSlug) return [];
    const base = `/${orgSlug}`;
    let results: LinkResult[] = [];
    if (scope === 'files') {
      results = (files.data ?? []).map((entry) => ({
        id: entry.path,
        title: entry.displayName ?? entry.name,
        href: workspaceFilesHref(orgSlug, entry.path),
        at: entry.updatedAt,
      }));
    } else if (scope === 'connectors') {
      results = [...(connectors.data ?? [])]
        .filter((card) => card.available || card.connected)
        .filter((card) => matches(term, card.label, card.toolkitSlug))
        .sort((left, right) => Number(right.connected) - Number(left.connected))
        .map((card) => ({
          id: card.toolkitSlug,
          title: card.label,
          href: `${base}/connectors/${encodeURIComponent(card.toolkitSlug)}`,
          hint: card.connected
            ? (card.providerAccountEmail ?? card.providerAccountLabel)
            : undefined,
        }));
    } else if (scope === 'brands') {
      results = (brands.data?.page ?? [])
        .filter((entry) => entry.kind === 'directory')
        .filter((entry) => matches(term, entry.displayName, entry.name))
        .map((entry) => {
          const path = `/${orgSlug}/brands/${entry.name}`;
          return {
            id: path,
            title: entry.displayName ?? entry.name,
            href: workspaceFilesHref(orgSlug, path),
            at: entry.modifiedAt,
          };
        });
    } else if (scope === 'drives') {
      results = (drives.data ?? [])
        .filter((drive) => matches(term, drive.name, drive.slug))
        .map((drive) => ({
          id: drive.slug,
          title: drive.name,
          href: workspaceFilesHref(orgSlug, drive.rootPath),
        }));
    } else if (scope === 'shared') {
      results = (shared.data?.entries ?? [])
        .filter((entry) => matches(term, entry.name, entry.path))
        .map((entry) => ({
          id: String(entry.nodeId),
          title: entry.name,
          href: workspaceFilesHref(orgSlug, entry.path),
          at: entry.sharedAt,
        }));
    } else if (scope === 'references') {
      results = (references.data?.items ?? []).map((reference) => ({
        id: String(reference._id),
        title: reference.title,
        href: `${base}/references`,
        at: reference._creationTime,
      }));
    } else if (scope === 'channels') {
      results = (channels.data?.joined ?? [])
        .filter((entry) => matches(term, entry.channel.name))
        .map((entry) => ({
          id: String(entry.channel._id),
          title: entry.channel.name,
          href: `${base}/chats/${encodeURIComponent(String(entry.channel._id))}`,
          hint: entry.unreadCount > 0 ? String(entry.unreadCount) : undefined,
          at: entry.lastMessageAt ?? undefined,
        }));
    } else if (scope === 'downloads') {
      results = (downloads.data ?? [])
        .filter((job) => matches(term, job.archiveName))
        .map((job) => ({
          id: String(job.jobId),
          title: job.archiveName,
          href: `${base}/downloads`,
          hint:
            job.progressPercent < 100
              ? `${Math.round(job.progressPercent)}%`
              : undefined,
          at: job.deliveredAt ?? job.startedAt,
        }));
    }
    return results.slice(0, LINK_LIMIT);
  }, [
    brands.data,
    channels.data,
    connectors.data,
    downloads.data,
    drives.data,
    files.data,
    orgSlug,
    references.data,
    scope,
    shared.data,
    term,
  ]);
  const pendingByScope: Record<LinkScope, boolean> = {
    files: term.trim().length >= 2 && files.data === undefined,
    connectors: connectors.data === undefined,
    brands: brands.data === undefined,
    drives: drives.data === undefined,
    shared: shared.data === undefined,
    references: references.data === undefined,
    channels: channels.data === undefined,
    downloads: downloads.data === undefined,
  };
  const isPending = Boolean(scope && orgSlug && pendingByScope[scope]);
  return { results, isPending };
}
